/* eslint-disable react/jsx-no-target-blank */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Tag,
  DatePicker,
  Select,
  Space,
  Tooltip,
  Switch,
  Typography,
  Avatar,
  Image,
  Tabs,
  Spin,
} from "antd";

import { parsePhoneNumberFromString } from "libphonenumber-js";
import { getExampleNumber } from "libphonenumber-js/max";
import examples from "libphonenumber-js/examples.mobile.json";
import {
  PlusOutlined,
  UserOutlined,
  TeamOutlined,
  IdcardOutlined,
  ExclamationCircleOutlined,
  FilterFilled,
} from "@ant-design/icons";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import useDebounce from "../hooks/useDebounce";
import lang from "../helper/lang";
import { Eye, Pencil, Trash2 } from "lucide-react";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import SuspendIcon from "../assets/images/suspend-main-icon.svg";
import CheckImgIcon from "../assets/images/activee-main-icon.svg";
import SingleImageUpload from "../components/SingleImageUpload";
import notfound from "../assets/images/not_found.png";
import PhoneInput from "react-phone-input-2";
import moment from "moment";
import WalletModal from "../components/WalletModal";
import UpdatePasswordModal from "../components/UpdatePasswordModal";
import UserProfileModal from "../components/UserProfileModal";
import MerchantProfileModal from "../components/MerchantProfileModal";
import {
  MINIMUM_AGE,
  getMaximumDobByAge,
  createMinimumAgeValidator,
} from "../helper/validation";

const { Search } = Input;
const { Text } = Typography;
const { confirm } = Modal;
const { RangePicker } = DatePicker;
const PRONOUN_OPTIONS = ["Dr.", "Mr.", "Ms.", "Mrs."].map((pr) => ({
  label: pr,
  value: pr,
}));

const GENDER_OPTIONS = [
  { label: "Male", value: "MALE" },
  { label: "Female", value: "FEMALE" },
  { label: "Other", value: "OTHERS" },
  { label: "Prefer not to say", value: "PREFER_NOT_TO_SAY" },
];

const USER_TYPE_OPTIONS = [{ label: "Individual User", value: "USER" }];

function UserManager() {
  const [searchParams] = useSearchParams();
  const { request } = useRequest();
  const maxAllowedDob = getMaximumDobByAge(MINIMUM_AGE);
  const validateMinimumAge = createMinimumAgeValidator(MINIMUM_AGE, "User");

  // State management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [walletLoading, setWalletLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [countryCode, setCountryCode] = useState("");
  const [image, setImage] = useState();
  const [logo, setLogo] = useState();
  const [cover, setCover] = useState();
  const [doc, setDoc] = useState();
  const [modalType, setModalType] = useState("add");
  const [form] = Form.useForm();
  const [form2] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const FileType = ["image/png", "image/jpg", "image/jpeg", "image/webp"];
  const merchantFileType = [
    // PDF
    "application/pdf",

    // Excel
    "application/vnd.ms-excel", // .xls
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx

    // Images
    "image/png",
    "image/jpg",
    "image/jpeg",
    "image/webp"
  ];
  const [filters, setFilters] = useState({
    status: null,
    sort: null,
    dateRange: null,
    kyc_status: null,
  });
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [ownerType, setOwnerType] = useState(() => {
    if (typeof window === "undefined") return "users";
    const tab = new URLSearchParams(window.location.search).get("tab");
    return tab === "merchants" ? "merchants" : "users";
  });
  // Loading states for different operations
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState({});
  const [statusToggleLoading, setStatusToggleLoading] = useState({});
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [changePasswordModal, setChangePasswordModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const debouncedSearchText = useDebounce(searchText, 500);
  const isInitialRender = useRef(true);
  const [, setPhoneCountry] = useState(null);
  const [phoneCountryIso, setPhoneCountryIso] = useState(null);
  const [walletModalVisible, setWalletModalVisible] = useState(false);
  const [addFundsLoading, setAddFundsLoading] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedUserSearch = useDebounce(search, 500);
  const moduleAccess = {
    can_view: true,
    can_create: true,
    can_edit: true,
    can_delete: true,
  };
  const [currentAdminRole, setCurrentAdminRole] = useState(moduleAccess);
  const [merchantCommentUpdateLoading, setMerchantCommentUpdateLoading] =
    useState(false);

  const [hasTrustAccountPermission, setHasTrustAccountPermission] = useState(false);

  useEffect(() => {
    try {
      const storedUserProfile = localStorage.getItem("user");
      const parsedProfile = storedUserProfile ? JSON.parse(storedUserProfile) : null;
      if (parsedProfile?.role?.module_permissions) {
        const trustAccountModule = parsedProfile.role.module_permissions.find(item => item.module === "trust_account");
        setHasTrustAccountPermission(!!trustAccountModule?.can_edit);
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    try {
      const storedUserProfile = localStorage.getItem("user");
      const parsedProfile = storedUserProfile ? JSON.parse(storedUserProfile) : null;
      if (parsedProfile?.role?.module_permissions) {
        // Map ownerType to actual module name in permissions
        const moduleMapping = {
          "merchants": "merchant",
          "users": "users"
        };
        const moduleName = moduleMapping[ownerType] || ownerType;

        const usersModule = parsedProfile?.role?.module_permissions?.find(item => item.module === moduleName);
        if (usersModule) {
          setCurrentAdminRole(usersModule);
        } else {
          setCurrentAdminRole(moduleAccess);
        }
      }
    } catch (error) {
      ShowToast("Failed to fetch user roles", Severty.ERROR);
      setCurrentAdminRole(moduleAccess);
    }
  }, [ownerType]);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "merchants") setOwnerType("merchants");
    else if (tab === "users") setOwnerType("users");
  }, [searchParams]);

  // Helper function to map API status to boolean
  const mapStatusToBoolean = (status) => {
    return status === "Active" || status === "active";
  };

  // Fetch users
  const fetchUsers = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.status === true) {
      params.append("status", "Active");
    } else if (filters.status === false) {
      params.append("status", "Inactive");
    } else if (filters.status) {
      params.append("status", filters.status);
    } else {
      params.append("status", "");
    }
    if (filters.kyc_status) {
      params.append(ownerType === "users" ? "kyc_status" : "kyb_status", filters.kyc_status);
    }
    if (filters.sort) {
      params.append("sort", filters.sort);
    }
    if (filters.dateRange?.length === 2) {
      try {
        // RangePicker returns dayjs objects which have format method
        params.append("start_date", filters.dateRange[0].format("YYYY-MM-DD"));
        params.append("end_date", filters.dateRange[1].format("YYYY-MM-DD"));
      } catch (e) {
        console.error("Date formatting error:", e);
        // Fallback: try to get date string in different ways
        try {
          if (
            filters.dateRange[0] &&
            typeof filters.dateRange[0].toISOString === "function"
          ) {
            params.append(
              "start_date",
              filters.dateRange[0].toISOString().split("T")[0],
            );
            params.append(
              "end_date",
              filters.dateRange[1].toISOString().split("T")[0],
            );
          } else if (
            filters.dateRange[0] &&
            typeof filters.dateRange[0].toDate === "function"
          ) {
            // If it's a dayjs object with toDate method
            params.append(
              "startDate",
              filters.dateRange[0].toDate().toISOString().split("T")[0],
            );
            params.append(
              "endDate",
              filters.dateRange[1].toDate().toISOString().split("T")[0],
            );
          }
        } catch (fallbackError) {
          console.error("Fallback date formatting also failed:", fallbackError);
        }
      }
    }
    if (debouncedSearchText) params.append("search", debouncedSearchText);
    params.append("page", pagination.current);
    params.append("limit", pagination.pageSize);
    const endPoint = ownerType === "users" ? "users" : "merchant-profile";
    request({
      url: `/api/admin/${endPoint}?${params.toString()}`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        let usersData = [];
        let totalCount = 0;
        // let currentPageFromApi = undefined;
        // let limitFromApi = undefined;

        if (data?.data) {
          // Common shapes: { data: { users, pagination } } or { data: { docs, totalDocs } }
          if (Array.isArray(data.data)) {
            usersData = data.data;
          }

          const meta = data.data.pagination || data.pagination || {};
          totalCount =
            meta.totalCount ?? meta.total ?? data.data.totalDocs ?? 0;
          // currentPageFromApi = meta.currentPage ?? meta.page ?? meta.pageNumber;
          // limitFromApi = meta.limit ?? meta.pageSize;
        } else if (Array.isArray(data)) {
          usersData = data;
          totalCount = data.length;
        }

        setUsers(Array.isArray(usersData) ? usersData : []);
        // Keep current/pageSize from UI state to avoid double-fetch loops
        // when API returns same values with different types (e.g. "1" vs 1).
        setPagination((prev) => ({
          ...prev,
          total: totalCount,
        }));
      },
      onError: (error) => {
        setLoading(false);
        setUsers([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        ShowToast(
          `${error.message ||
          error.response.data.message ||
          error.response.message ||
          "Failed to terminate session"
          }`,
          Severty.ERROR,
        );
      },
    });
  }, [
    filters,
    ownerType,
    debouncedSearchText,
    pagination.current,
    pagination.pageSize,
    request,
  ]);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    // When clearing search, wait for debounced value to sync to avoid double API calls
    if (searchText === "" && debouncedSearchText !== "") {
      return;
    }
    fetchUsers();
  }, [
    filters,
    ownerType,
    debouncedSearchText,
    pagination.current,
    pagination.pageSize,
  ]);

  // Handlers
  const handleTableChange = (paginationInfo, filters, sorter) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));
  };

  const handleRemoveImage = () => {
    setImage(null); // clear image preview
    form.setFieldsValue({ image: null }); // clear form field
  };

  const handleRemoveLogo = () => {
    setLogo(null); // clear image preview
    form.setFieldsValue({ logo: null }); // clear form field
  };
  const handleRemoveCover = () => {
    setCover(null); // clear image preview
    form.setFieldsValue({ cover: null }); // clear form field
  };
  const handleRemoveDocument = () => {
    setDoc(null); // clear image preview
    form.setFieldsValue({ doc: null }); // clear form field
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleAddUser = () => {
    if (!currentAdminRole?.can_create) {
      ShowToast("You are not authorized to add users", Severty.ERROR);
      return;
    }

    setModalType("add");
    form.resetFields();

    // 🔥 reset phone state
    setPhoneCountry({ dialCode: "64" });
    setPhoneCountryIso("NZ");
    setCountryCode("64");
    setImage(null);
    setLogo(null);
    setCover(null);
    setDoc(null);

    setModalVisible(true);
  };

  const isValidPhoneNumber = (phone, countryCode) => {
    if (!phone || !countryCode) return false;

    const phoneNumber = parsePhoneNumberFromString(
      phone,
      countryCode.toUpperCase(),
    );

    return phoneNumber ? phoneNumber.isValid() : false;
  };

  const getPhoneExample = (countryCode) => {
    console.log("country_code==>", countryCode);
    if (countryCode === "NZ") {
      return "+64 xxx xxx xx"; // ✅ clean & correct NZ format
    }
    try {
      const example = getExampleNumber(countryCode, examples);
      if (!example) return null;

      const intl = example.formatInternational();
      // "+91 62654 53127"

      // keep + and spaces, mask digits except country code
      // const countryCallingCode = example.countryCallingCode;

      let seenCountryCode = false;

      return intl.replace(/\d+/g, (match) => {
        // first numeric chunk is country calling code
        if (!seenCountryCode) {
          seenCountryCode = true;
          return match; // keep country code
        }
        return "x".repeat(match.length);
      });
    } catch {
      return null;
    }
  };

  const fetchWalletDetails = (
    userId,
    { openModal = false, silent = false } = {},
  ) => {
    if (!userId) return;

    if (!silent) {
      setWalletLoading(true);
    }

    request({
      url: `/api/admin/wallet/${userId}`,
      method: "GET",
      onSuccess: (res) => {
        const data = res?.data;

        setWalletData(data?.wallet || null);
        setTransactions(data?.transactions || []);

        if (openModal) {
          setWalletModalVisible(true);
        }
        setWalletLoading(false);
      },
      onError: (error) => {
        setWalletLoading(false);
        ShowToast(
          error?.response?.data?.message || "Failed to fetch wallet details",
          Severty.ERROR,
        );
      },
    });
  };

  const handleWalletModal = (walletFromProfile = null) => {

    if (!walletFromProfile) return;
    const walletId = walletFromProfile.walletId || walletFromProfile.id;

    if (walletFromProfile) {
      setWalletData(walletFromProfile);
      setWalletModalVisible(true);
    }

    fetchWalletDetails(walletId, { openModal: true });
  };

  const handleAddFunds = ({ amount, reason, note, transactionType = "credit" }) => {
    return new Promise((resolve, reject) => {
      setAddFundsLoading(true);
      const userId = walletData?.walletId || walletData?.id;

      request({
        url: `/api/admin/wallet/${userId}`,
        method: "PUT",
        data: {
          added_funds: Number(amount),
          reason,
          note,
          transaction_type: transactionType
        },
        onSuccess: (res) => {
          const updatedWallet = res?.data;
          ShowToast(res?.message ?? "Funds added successfully", Severty.SUCCESS);

          // ✅ update selected user fully
          setSelectedUser((prev) => ({
            ...prev,
            wallet: updatedWallet,
          }));

          // optional: refresh user list
          fetchUsers();
          // refresh wallet + recent transactions instantly in open modal
          fetchWalletDetails(userId, { silent: true });

          setAddFundsLoading(false);
          resolve(updatedWallet); // 🔥 THIS IS KEY
        },
        onError: (error) => {
          setAddFundsLoading(false);
          ShowToast(
            error?.response?.data?.message || "Failed to add funds",
            Severty.ERROR,
          );
          reject(error);
        },
      });
    });
  };

  const handleEditUser = (user) => {
    if (!currentAdminRole?.can_edit) {
      ShowToast("You are not authorized to edit users", Severty.ERROR);
      return;
    }
    setSelectedUser(user);
    setModalType("edit");
    setModalVisible(true);
    if (ownerType === "merchants") {
      const primaryUser = user?.users?.find(u => u?.is_primary && u?.role?.toUpperCase() === "OWNER")?.user ?? null;
      
      const initialDirectors = Array.isArray(user?.directors)
        ? user.directors.map(d => ({ name: typeof d === "object" ? d?.name || "" : d }))
        : [];
      const initialShareholders = Array.isArray(user?.share_holders)
        ? user.share_holders.map(sh => ({
            name: typeof sh === "object" ? sh?.name || "" : sh,
            percent_holding: typeof sh === "object" ? sh?.percent_holding ?? "" : 0,
          }))
        : [];

      form.setFieldsValue({
        nzbn: user.merchant_code,
        business_name: user.business_name,
        refund_policy: user.refund_policy,
        business_type: user.business_type,
        invoicing_name: user.invoicing_name || "",
        gst_number: user.gst_number || "",
        invoicing_email: user.invoicing_email || "",
        user_id: primaryUser
          ? {
              label: `${primaryUser.first_name || ""} ${primaryUser.last_name || ""} (${primaryUser.email || ""})`,
              value: primaryUser.id,
            }
          : undefined,
        business_logo: user.business_logo,
        business_cover_img: user.business_cover_img,
        registration_doc: user.registration_doc,
        kyb_status: user.kyb_status || "PENDING",
        admin_comments: user.admin_comments || "",
        directors: initialDirectors,
        share_holders: initialShareholders,
      });

      // Always fetch fresh merchant detail in case row didn't have full directors/shareholders
      request({
        url: `/api/admin/merchant-profile/${user.id}`,
        method: "GET",
        onSuccess: (res) => {
          const detail = res?.data;
          if (detail) {
            setSelectedUser(detail);
            const detailDirectors = Array.isArray(detail?.directors)
              ? detail.directors.map(d => ({ name: typeof d === "object" ? d?.name || "" : d }))
              : [];
            const detailShareholders = Array.isArray(detail?.share_holders)
              ? detail.share_holders.map(sh => ({
                  name: typeof sh === "object" ? sh?.name || "" : sh,
                  percent_holding: typeof sh === "object" ? sh?.percent_holding ?? "" : 0,
                }))
              : [];

            form.setFieldsValue({
              directors: detailDirectors,
              share_holders: detailShareholders,
              kyb_status: detail.kyb_status || "PENDING",
              admin_comments: detail.admin_comments || "",
            });
          }
        },
        onError: () => {},
      });
    }
    if (ownerType === "users") {
      form.setFieldsValue({
        first_name: user.first_name,
        middle_name: user.middle_name,
        last_name: user.last_name,
        username: user.username || "",
        email: user.email,
        mobile: `${user.country_code}${user.mobile}` || user.phone,
        isActive: user.status === "Active" ? true : false,

        pronoun: user.pronoun,
        gender: user.gender,
        blood_group: user.blood_group,
        user_type: user.user_type || USER_TYPE_OPTIONS[0].value,
        date_of_birth: user.date_of_birth ? moment(user.date_of_birth) : null,
      });
    }
    setCountryCode(user.country_code || "");
    if (ownerType === "users") {
      setImage(user.profile_img || null);
    } else {
      setLogo(user.business_logo || null);
      setCover(user.business_cover_img || null);
      setDoc(user.registration_doc || null);
    }
  };

  const handleViewProfile = (user) => {
    if (!currentAdminRole?.can_view) {
      ShowToast("You are not authorized to view users", Severty.ERROR);
      return;
    }
    request({
      url: `/api/admin/${ownerType === "users" ? "users" : "merchant-profile"}/${user.id}`,
      method: "GET",
      onSuccess: (res) => {
        setSelectedUser(res.data);
        setModalType("view");
        setModalVisible(true);
      },
      onError: (error) => {
        ShowToast(error.message, Severty.ERROR);
      },
    });

  };

  const handleImage = (data) => {
    data.length > 0 ? setImage(data?.[0]?.url) : setImage("");
  };

  const handleLogo = (data) => {
    data.length > 0 ? setLogo(data?.[0]?.url) : setLogo("");
  };

  const handleCover = (data) => {
    data.length > 0 ? setCover(data?.[0]?.url) : setCover("");
  };

  const handleDoc = (data) => {
    data.length > 0 ? setDoc(data?.[0]?.url) : setDoc("");
  };

  //   ------------ USER Create and Update ----------
  const handleFormSubmit = async (values) => {
    try {
      if (modalType === "add" && !currentAdminRole?.can_create) {
        ShowToast("You are not authorized to add users", Severty.ERROR);
        setModalVisible(false);
        return;
      }
      let userData = {};
      if (ownerType === "users") {
        userData = {
          first_name: values.first_name?.trim() || null,
          middle_name: values.middle_name?.trim() || null,
          last_name: values.last_name?.trim() || null,
          // username: values.username,
          user_type: "USER",
          email: values.email,
          mobile: values.mobile.slice(countryCode.length),
          country_code: countryCode,
          profile_img: image,
          status: values.isActive === true ? "Active" : "Suspended",
          pronoun: values.pronoun ?? null,
          gender: values.gender ?? form.getFieldValue("gender") ?? null,
          blood_group: values.blood_group,
          date_of_birth: values.date_of_birth
            ? values.date_of_birth.format("YYYY-MM-DD")
            : null,
        };
      } else {
        const formattedDirectors = (values.directors || [])
          .filter((d) => d && d.name && d.name.trim())
          .map((d) => ({ name: d.name.trim() }));

        const formattedShareholders = (values.share_holders || [])
          .filter((sh) => sh && sh.name && sh.name.trim())
          .map((sh) => ({
            name: sh.name.trim(),
            percent_holding:
              sh.percent_holding !== undefined &&
              sh.percent_holding !== null &&
              sh.percent_holding !== ""
                ? Number(sh.percent_holding)
                : 0,
          }));

        userData = {
          nzbn: values.nzbn?.trim() || null,
          business_name: values.business_name?.trim() || null,
          business_logo: logo,
          business_cover_img: cover,
          registration_doc: doc,
          refund_policy: values.refund_policy?.trim() || null,
          business_type: values.business_type?.trim() || null,
          user_id: values.user_id,
          invoicing_name: values.invoicing_name?.trim() || null,
          gst_number: values.gst_number?.trim() || null,
          invoicing_email: values.invoicing_email?.trim() || null,
          directors: formattedDirectors,
          share_holders: formattedShareholders,
        };

        if (values.kyb_status) {
          userData.kyb_status = values.kyb_status;
        }
        if (values.admin_comments !== undefined) {
          userData.admin_comments = values.admin_comments;
        }
      }
      // Add password only when creating a new user
      if (modalType === "add" && values.password) {
        userData.password = values.password;
      }

      if (modalType === "add") {
        setCreateLoading(true);
        request({
          url: `/api/admin/${ownerType === "users" ? "users" : "merchant-profile"}`,
          method: "POST",
          data: userData,
          onSuccess: () => {
            ShowToast("User created successfully", Severty.SUCCESS);
            setImage(null); // ✅ explicitly null
            setLogo(null);
            setCover(null);
            setDoc(null);
            form.resetFields();
            setModalVisible(false);
            fetchUsers();
            setCreateLoading(false);
            setPhoneCountryIso(null);
          },
          onError: (error) => {
            console.log("error=====>", error);
            console.log("error_response=====>", error.response);
            ShowToast(
              error?.response?.data?.detail || error?.response?.data?.message || "Failed to update user",
              Severty.ERROR,
            );
            setModalVisible(false);
            setCreateLoading(false);
          },
        });
      } else {
        setUpdateLoading(true);
        request({
          url: `/api/admin/${ownerType === "users" ? "users" : "merchant-profile"}/${selectedUser._id || selectedUser.id}`,
          method: "PUT",
          data: userData,
          onSuccess: () => {
            ShowToast("User updated successfully", Severty.SUCCESS);
            setModalVisible(false);
            fetchUsers();
            setUpdateLoading(false);
          },
          onError: (error) => {
            ShowToast(
              error?.response?.data?.detail || error?.response?.data?.message || "Failed to update user",
              Severty.ERROR,
            );
            setUpdateLoading(false);
          },
        });
      }
    } catch (error) {
      ShowToast("Failed to save user", Severty.ERROR);
      setCreateLoading(false);
      setUpdateLoading(false);
    }
  };

  const resetUserModalState = () => {
    setModalVisible(false);
    form.resetFields();
    // 🔥 THIS IS THE KEY
    setPhoneCountry(null);
    setPhoneCountryIso(null);
    setCountryCode("");
    setImage(null);
    setLogo(null);
    setCover(null);
    setDoc(null);
  };

  const handleDeleteUser = async (user, reason) => {
    try {
      if (!currentAdminRole?.can_delete) {
        ShowToast("You are not authorized to delete users", Severty.ERROR);
        return;
      }
      const userId = user._id || user.id;
      setDeleteLoading((prev) => ({ ...prev, [userId]: true }));
      let params = new URLSearchParams();
      params.append("action", "closure");
      request({
        url: `/api/admin/users/${userId}?${params.toString()}`,
        method: "DELETE",
        data: { reason: reason },
        onSuccess: (data) => {
          ShowToast("User is initiated for closure successfully", Severty.SUCCESS);
          setDeleteLoading((prev) => ({ ...prev, [userId]: false }));
          fetchUsers();
        },
        onError: (error) => {
          ShowToast(
            `${error.message ||
            error.response.data.message ||
            error.response.message ||
            "Failed to terminate session"
            }`,
            Severty.ERROR,
          );
          setDeleteLoading((prev) => ({ ...prev, [userId]: false }));
        },
      });
    } catch (error) {
      ShowToast(
        `${error.message ||
        error.response.data.message ||
        error.response.message ||
        "Failed to terminate session"
        }`,
        Severty.ERROR,
      );
      const userId = user._id || user.id;
      setDeleteLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const resetDeleteModal = () => {
    setDeleteModalVisible(false);
    setUserToDelete(null);
  };

  const handleStatusToggle = async (user, newStatus) => {
    try {
      if (!currentAdminRole?.can_edit) {
        ShowToast("You are not authorized to update user status", Severty.ERROR);
        return;
      }
      const userId = user._id || user.id;
      setStatusToggleLoading((prev) => ({ ...prev, [userId]: true }));
      let params = new URLSearchParams();
      params.append("action", newStatus === true ? "active" : "inactive");
      request({
        url: `/api/admin/${ownerType === "users" ? "users" : "merchant-profile"}/${userId}?${params.toString()}`,
        method: "DELETE",
        onSuccess: () => {
          ShowToast(
            `User ${newStatus ? "activated" : "suspended"} successfully`,
            Severty.SUCCESS,
          );
          fetchUsers();
          setStatusToggleLoading((prev) => ({ ...prev, [userId]: false }));
        },
        onError: (error) => {
          ShowToast(
            `${error.message ||
            error.response.data.message ||
            error.response.message ||
            "Failed to terminate session"
            }`,
            Severty.ERROR,
          );
          setStatusToggleLoading((prev) => ({ ...prev, [userId]: false }));
        },
      });
    } catch (error) {
      ShowToast("Failed to update user status", Severty.ERROR);
      const userId = user._id || user.id;
      setStatusToggleLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const handleUpdateUserPassword = async (values) => {
    try {
      setUpdateLoading(true);

      const payload = {
        user_id: selectedUser?.id,
        new_password: values.password,
        confirm_new_password: values.confirm_password,
      };
      // Reset the password with the new password entered by the user
      await request({
        url: "/auth/change-password",
        method: "POST",
        data: payload,
        onSuccess: (data) => {
          ShowToast("Password has been changed successfully", Severty.SUCCESS);

          form2.resetFields();
          setChangePasswordModal(false);
          setUpdateLoading(false);
        },
        onError: (errorMessage) => {
          setUpdateLoading(false);
        },
      });
    } catch (error) {
      console.log("Error occurred while updating password", error);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleUpdateMerchantAdminComments = (record, adminComments) => {
    const merchantId = record?._id || record?.id;
    if (!merchantId) return Promise.resolve(false);

    return new Promise((resolve) => {
      setMerchantCommentUpdateLoading(true);
      request({
        url: `/api/admin/merchant-profile/${merchantId}`,
        method: "PUT",
        data: {
          admin_comments: adminComments,
        },
        onSuccess: () => {
          ShowToast("Merchant comment updated successfully", Severty.SUCCESS);
          setUsers((prev) =>
            prev.map((item) => {
              const id = item?._id || item?.id;
              if (id !== merchantId) return item;
              return {
                ...item,
                admin_comments: adminComments,
              };
            }),
          );
          setSelectedUser((prev) => {
            if (!prev) return prev;
            const id = prev?._id || prev?.id;
            if (id !== merchantId) return prev;
            return {
              ...prev,
              admin_comments: adminComments,
            };
          });
          setMerchantCommentUpdateLoading(false);
          resolve(true);
        },
        onError: (error) => {
          ShowToast(
            error?.response?.data?.detail ||
            error?.response?.data?.message ||
            "Failed to update merchant comment",
            Severty.ERROR,
          );
          setMerchantCommentUpdateLoading(false);
          resolve(false);
        },
      });
    });
  };
  // -----------------------------
  // STATUS COLOR MAP
  // -----------------------------
  const statusColor = {
    PENDING: "orange",
    "UNDER REVIEW": "blue",
    COMPLETED: "green",
    REJECTED: "red",
    CANCELLED: "volcano",
    EXPIRED: "red",
  };

  const fetchUsersForMerchant = async (searchText = "", pageNo = 1) => {
    setUserLoading(true);

    request({
      url: `/api/admin/users?search=${searchText}&page=${pageNo}&limit=10`,
      method: "GET",
      onSuccess: (res) => {
        const users = res?.data || [];

        const mapped = users.map((u) => ({
          label: `${u.first_name} ${u.last_name} (${u.email})`,
          value: u.id,
        }));
        setUserOptions((prev) => {
          const selected = form.getFieldValue("user_id");
          if (selected && selected.value) {
            const exists = mapped.some((opt) => opt.value === selected.value);
            if (!exists) {
              mapped.unshift({
                label: selected.label,
                value: selected.value,
              });
            }
          }

          return mapped;
        });

        setHasMore(users.length === 10);
        setUserLoading(false);
      },
      onError: () => setUserLoading(false),
    });
  };

  useEffect(() => {

    setUserOptions((prev) => {

      const selected = form.getFieldValue("user_id");
      if (selected && selected.value) {
        const exists = prev.some((opt) => opt.value === selected.value);

        if (!exists) {
          return [
            {
              label: selected.label,
              value: selected.value,
            },
            ...prev,
          ];
        }
      }

      return prev;
    });
    setHasMore(true);
    setPage(1);

    fetchUsersForMerchant(debouncedUserSearch, 1);
  }, [debouncedUserSearch]);

  const handleUserSearch = (value) => {
    setSearch(value);
    setPage(1);
    // fetchUsersForMerchant(value, 1);
  };



  // Table columns for Users
  const getColumns = () => [
    {
      title: "S. No.",
      key: "serial_no",
      width: 80,
      render: (_, __, index) => {
        const serialNumber =
          (pagination.current - 1) * pagination.pageSize + index + 1;
        return <Text>{serialNumber}</Text>;
      },
    },
    {
      title: "Customer ID",
      dataIndex: "id",
      key: "id",
      width: 120,
      render: (id) => <Text code>{id}</Text>,
    },
    {
      title: "Name",
      dataIndex: ["first_name", "middle_name", "last_name"],
      key: "name",
      width: 200,
      sorter: (a, b) => {
        const nameA = a?.first_name?.toLowerCase() + " " + a?.middle_name?.toLowerCase() + " " + a?.last_name?.toLowerCase() || "";
        const nameB = b?.first_name?.toLowerCase() + " " + b?.middle_name?.toLowerCase() + " " + b?.last_name?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      },
      render: (name, record) => (
        < div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {record?.profile_img || record?.user?.profile_img || record?.user?.avatar ? (
            <img 
              src={record?.profile_img || record?.user?.profile_img || record?.user?.avatar}
              alt="avatar"
              style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", objectPosition: "center" }}
            />
          ) : (
            <Avatar
              icon={<UserOutlined />}
              style={{ backgroundColor: "#1890ff" }}
            />
          )}
          <span>{name || (record?.first_name ?? "") + " " + (record?.middle_name ?? "") + " " + (record?.last_name ?? "") || "N/A"}</span>
        </div >
      ),
    },
    {
      title: lang("Status") || "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status, record) => {
        const isActive = mapStatusToBoolean(status || record.isActive);
        return (
          <Tag color={isActive ? "green" : "red"}>
            {status || (record.isActive ? "Active" : "In Active")}
          </Tag>
        );
      },
      onFilter: (value, record) =>
        mapStatusToBoolean(record.status || record.is_active) === value,
    },

    {
      title: "KYC Status",
      dataIndex: "kyc_status",
      key: "kyc_status",
      render: (_, { kyc_status }) => {
        return <Tag color={statusColor[kyc_status]}>{kyc_status ? kyc_status : "No"}</Tag>;
      }
    },

    {
      title: "KYC Date",
      dataIndex: "kyc_approved_at",
      key: "kyc_approved_at",
      width: 120,
      sorter: (a, b) =>
        moment(a.kyc_approved_at).valueOf() - moment(b.kyc_approved_at).valueOf(),
      render: (kycApprovedAt) =>
        kycApprovedAt ? moment(kycApprovedAt).format("DD MMM YYYY, hh:mm A") : "N/A",
    },

    {
      title: "Registration Date",
      dataIndex: "join_date",
      key: "join_date",
      width: 120,
      sorter: (a, b) =>
        moment(a.join_date).valueOf() - moment(b.join_date).valueOf(),
      render: (date) => (date ? moment(date).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Last Login",
      dataIndex: "last_login",
      key: "last_login",
      width: 120,
      render: (last_login) => {
        return <span>{last_login ? moment(last_login).format("DD MMM YYYY, hh:mm A") : "-"}</span>;
      },
    },
    {
      title: lang("Actions") || "Actions",
      key: "actions",
      width: 250,
      render: (_, record) => (
        <Space size="small">
          {currentAdminRole?.can_view && <Tooltip title={lang("View_Profile") || "View Profile"}>
            <Button
              className="button-action-small"
              icon={<Eye />}
              onClick={() => handleViewProfile(record)}
              disabled={
                statusToggleLoading[record._id || record.id] ||
                deleteLoading[record._id || record.id]
              }
            />
          </Tooltip>}
          {currentAdminRole?.can_edit && <Tooltip title={lang("Edit") || "Edit"}>
            <Button
              className="button-action-small"
              icon={<Pencil />}
              onClick={() => handleEditUser(record)}
              disabled={
                statusToggleLoading[record._id || record.id] ||
                deleteLoading[record._id || record.id]
              }
            />
          </Tooltip>}
          {currentAdminRole?.can_edit && mapStatusToBoolean(record.status || record.isActive) ? (
            <Tooltip title={"Deactivate"}>
              <Button
                className="button-action-small"
                icon={<img src={SuspendIcon} alt="suspended" />}
                style={{ color: "#faad14" }}
                loading={statusToggleLoading[record._id || record.id]}
                disabled={
                  statusToggleLoading[record._id || record.id] ||
                  deleteLoading[record._id || record.id]
                }
                onClick={() =>
                  showUserStatusConfirm(
                    record,
                    true,
                    handleStatusToggle,
                    lang,
                    statusToggleLoading,
                    deleteLoading,
                  )
                }
              />
            </Tooltip>
          ) : currentAdminRole?.can_edit && (
            <Tooltip title={lang("Activate") || "Activate"}>
              <Button
                className="button-action-small"
                icon={<img src={CheckImgIcon} alt="activate" />}
                style={{ color: "#52c41a" }}
                loading={statusToggleLoading[record._id || record.id]}
                disabled={
                  statusToggleLoading[record._id || record.id] ||
                  deleteLoading[record._id || record.id]
                }
                onClick={() =>
                  showUserStatusConfirm(
                    record,
                    false,
                    handleStatusToggle,
                    lang,
                    statusToggleLoading,
                    deleteLoading,
                  )
                }
              />
            </Tooltip>
          )}
          {currentAdminRole?.can_delete && <Tooltip title={lang("Delete") || "Delete"}>
            <Button
              className="button-action-small"
              icon={<Trash2 />}
              style={{ color: "#ff4d4f" }}
              loading={deleteLoading[record._id || record.id]}
              disabled={
                statusToggleLoading[record._id || record.id] ||
                deleteLoading[record._id || record.id]
              }
              onClick={() => {
                setUserToDelete(record);
                setDeleteModalVisible(true);
              }}
            />
          </Tooltip>}
        </Space>
      ),
    },
  ];

  // Table columns for Merchants
  const getMerchantsColumns = () => [
    {
      title: "S. No.",
      key: "serial_no",
      width: 80,
      render: (_, __, index) => {
        const serialNumber =
          (pagination.current - 1) * pagination.pageSize + index + 1;
        return <Text>{serialNumber}</Text>;
      },
    },
    {
      title: "Merchant ID",
      dataIndex: "id",
      key: "id",
      width: 120,
      render: (id) => <Text code>{id}</Text>,
    },
    {
      title: "NZBN",
      dataIndex: "merchant_code",
      key: "merchant_code",
      width: 120,
      render: (merchant_code) => <Text code>{merchant_code}</Text>,
    },
    {
      title: "Merchant Name",
      dataIndex: "business_name",
      key: "business_name",
      width: 200,
      sorter: (a, b) => {
        const nameA = a?.business_name?.toLowerCase() || "";
        const nameB = b?.business_name?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      },
      render: (business_name, record) => {
        const logo = record?.business_logo;

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {logo ? (
              <img
                src={logo}
                alt={business_name}
                style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", objectPosition: "center" }}
              />
            ) : (
              <Avatar size={32} icon={<UserOutlined />} style={{ backgroundColor: "#1890ff" }} />
            )}

            <span>{business_name || record?.business_name || "N/A"}</span>
          </div>
        );
      },
    },
    {
      title: lang("Status") || "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status, record) => {
        const isActive = record?.is_active ? true : false; //mapStatusToBoolean(record?.is_active);
        return (
          <Tag color={isActive ? "green" : "red"}>
            {status || (record.is_active ? "Active" : "In Active")}
          </Tag>
        );
      },
      onFilter: (value, record) =>
        mapStatusToBoolean(record.status || record.is_active) === value,
    },
    {
      title: "KYB Status",
      dataIndex: "kyb_status",
      key: "kyb_status",
      width: 120,
      render: (kyb_status) => {
        return <Tag color={statusColor[kyb_status]}>{kyb_status ? kyb_status : "No"}</Tag>;
      }
    },
    {
      title: "KYB Date",
      dataIndex: "kyb_approved_at",
      key: "kyb_approved_at",
      width: 150,
      render: (kybApprovedAt) =>
        kybApprovedAt ? moment(kybApprovedAt).format("DD MMM YYYY, hh:mm A") : "N/A",
    },
    {
      title: "Registration Date",
      dataIndex: "created_at",
      key: "created_at",
      width: 120,
      sorter: (a, b) =>
        moment(a.created_at).valueOf() - moment(b.created_at).valueOf(),
      render: (date) => (date ? moment(date).format("DD MMM YYYY") : "-"),
    },
    {
      title: lang("Actions") || "Actions",
      key: "actions",
      width: 250,
      render: (_, record) => (
        <Space size="small">

          <Tooltip title={lang("View_Profile") || "View Profile"}>
            <Button
              className="button-action-small"
              icon={<Eye />}
              onClick={() => handleViewProfile(record)}
              disabled={
                statusToggleLoading[record._id || record.id] ||
                deleteLoading[record._id || record.id]
              }
            />
          </Tooltip>

          {currentAdminRole?.can_edit && <Tooltip title={lang("Edit") || "Edit"}>
            <Button
              className="button-action-small"
              icon={<Pencil />}
              onClick={() => handleEditUser(record)}
              disabled={
                statusToggleLoading[record._id || record.id] ||
                deleteLoading[record._id || record.id]
              }
            />
          </Tooltip>}
          {(record.is_active || record.is_active) ? (
            <Tooltip title={"Deactivate"}>
              <Button
                className="button-action-small"
                icon={<img src={SuspendIcon} alt="inactive" />}
                style={{ color: "#faad14" }}
                loading={statusToggleLoading[record._id || record.id]}
                disabled={
                  statusToggleLoading[record._id || record.id] ||
                  deleteLoading[record._id || record.id]
                }
                onClick={() =>
                  showUserStatusConfirm(
                    record,
                    true,
                    handleStatusToggle,
                    lang,
                    statusToggleLoading,
                    deleteLoading,
                  )
                }
              />
            </Tooltip>
          ) : (
            <Tooltip title={lang("Activate") || "Activate"}>
              <Button
                className="button-action-small"
                icon={<img src={CheckImgIcon} alt="activate" />}
                style={{ color: "#52c41a" }}
                loading={statusToggleLoading[record._id || record.id]}
                disabled={
                  statusToggleLoading[record._id || record.id] ||
                  deleteLoading[record._id || record.id]
                }
                onClick={() =>
                  showUserStatusConfirm(
                    record,
                    false,
                    handleStatusToggle,
                    lang,
                    statusToggleLoading,
                    deleteLoading,
                  )
                }
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const showUserStatusConfirm = (
    record,
    isActive,
    handleStatusToggle,
    lang,
    statusToggleLoading,
    deleteLoading,
  ) => {
    const loading = statusToggleLoading[record._id || record.id];
    const disabled = loading || deleteLoading[record._id || record.id];

    confirm({
      title: isActive
        ? "Are you sure you want to deactivate this user?"
        : "Are you sure you want to activate this user?",
      okText: lang("Yes") || "Yes",
      cancelText: lang("No") || "No",
      okType: isActive ? "primary" : "primary",
      centered: true,
      okButtonProps: {
        loading,
        disabled,
      },
      onOk() {
        handleStatusToggle(record, !isActive);
      },
    });
  };

  return (
    <div>
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div>
              <TeamOutlined style={{ marginRight: 8 }} />
              {ownerType === "users" ? lang("User_Management") || "User Management" : lang("Merchant_Management") || "Merchant Management"}
              <p>
                {lang(
                  "Admin_manages_end_user_accounts_view_edit_suspend_or_delete_as_necessary",
                ) ||
                  "Admin manages end-user accounts: view, edit, suspend or delete as necessary."}
              </p>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                icon={<FilterFilled />}
                onClick={() => setFiltersVisible((prev) => !prev)}
              >
                {filtersVisible ? "Hide Filters" : "Show Filters"}
              </Button>
              {currentAdminRole?.can_create && <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddUser}
              >
                {ownerType === "users" ? lang("Add_User") || "Add User" : lang("Add_Merchant") || "Add Merchant"}
              </Button>}
            </div>
          </div>
        }
      >
        <Tabs
          activeKey={ownerType === "users" ? "users" : "merchants"}
          onChange={(key) => {
            const nextOwnerType = key === "users" ? "users" : "merchants";
            setModalVisible(false);
            setModalType("add");
            setSelectedUser(null);
            setOwnerType(nextOwnerType);
            setFilters({
              status: null,
              sort: null,
              dateRange: null,
              kyc_status: null,
            });
            setSearchText("");
            setPagination((prev) => ({ ...prev, current: 1 }));
          }}
          items={[
            { key: "users", label: "Users" },
            { key: "merchants", label: "Merchants" },
          ]}
        />


        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              alignItems: "center",
            }}
          >
            {filtersVisible && (
              <>
                <Search
                  placeholder={
                    ownerType === "users" ? lang("Search_by_name_or_email") || "Search by name or email" : lang("Search_by_merchant_name") || "Search by Merchant name"
                  }
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                  onSearch={handleSearch}
                  style={{ minWidth: 200, flex: "1 1 200px" }}
                />
                <Select
                  placeholder={ownerType === "users" ? "Filter by KYC Status" : "Filter by KYB Status"}
                  style={{ minWidth: 160, flex: "1 1 160px" }}
                  allowClear
                  value={filters.kyc_status}
                  onChange={(value) => handleFilterChange("kyc_status", value)}
                >
                  <Select.Option value={"COMPLETED"}>{"COMPLETED"}</Select.Option>
                  <Select.Option value={"UNDER REVIEW"}>{"UNDER REVIEW"}</Select.Option>
                  <Select.Option value={"PENDING"}>{"PENDING"}</Select.Option>
                  <Select.Option value={"REJECTED"}>{"REJECTED"}</Select.Option>
                  <Select.Option value={"CANCELLED"}>{"CANCELLED"}</Select.Option>
                  <Select.Option value={"EXPIRED"}>{"EXPIRED"}</Select.Option>
                </Select>
                <Select
                  placeholder={lang("Filter_by_Status") || "Filter by Status"}
                  style={{ minWidth: 160, flex: "1 1 160px" }}
                  allowClear
                  value={filters.status}
                  onChange={(value) => handleFilterChange("status", value)}
                >
                  <Select.Option value={true}>{"Active"}</Select.Option>
                  <Select.Option value={false}>{"Inactive"}</Select.Option>
                  {ownerType === "users" && <Select.Option value={"SUSPENDED"}>{"Suspended"}</Select.Option>}
                  {ownerType === "users" && <Select.Option value={"PENDING_TERMINATION"}>{"Pending Termination"}</Select.Option>}
                  {ownerType === "users" && <Select.Option value={"DELETED"}>{"Deleted"}</Select.Option>}
                </Select>
                <Select
                  placeholder={"Sorting"}
                  style={{ minWidth: 160, flex: "1 1 160px" }}
                  allowClear
                  value={filters.sort}
                  onChange={(value) => handleFilterChange("sort", value)}
                >
                  <Select.Option value={"name"}>{"Name Wise"}</Select.Option>
                  <Select.Option value={"status"}>
                    {"Status Wise"}
                  </Select.Option>
                  <Select.Option value={"newest"}>
                    {"Newest User"}
                  </Select.Option>
                  <Select.Option value={"oldest"}>
                    {"Oldest User"}
                  </Select.Option>
                </Select>
                <RangePicker
                  style={{ minWidth: 220, flex: "1 1 220px" }}
                  placeholder={[
                    lang("Start_Date") || "Start Date",
                    lang("End_Date") || "End Date",
                  ]}
                  value={filters.dateRange}
                  onChange={(dates) => handleFilterChange("dateRange", dates)}
                />
                <Button
                  icon={<ExclamationCircleOutlined />}
                  disabled={filters.status === null && filters.sort === null && filters.dateRange === null && filters.kyc_status === null && searchText === ""}
                  onClick={() => {
                    setFilters({
                      status: null,
                      sort: null,
                      dateRange: null,
                      kyc_status: null,
                    });
                    setSearchText("");
                    setPagination((prev) => ({ ...prev, current: 1 }));
                  }}
                >
                  {lang("Clear_Filters") || "Clear Filters"}
                </Button>
              </>
            )}
          </div>
        </div>
        {/* Users Table */}
        <div className="table-responsive">
          <Table
            columns={ownerType === "users" ? getColumns() : getMerchantsColumns()}
            dataSource={Array.isArray(users) ? users : []}
            rowKey="_id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total || 0,
              showSizeChanger: true,
              showQuickJumper: false,
              showTotal: (total, range) =>
                `${lang("Showing") || "Showing"} ${range[0]}-${range[1]} ${lang("of") || "of"
                } ${total} ${ownerType === "users" ? lang("users") || "users" : lang("merchants") || "merchants"}`,
              pageSizeOptions: ["10", "20", "30", "50"],
            }}
            loading={loading}
            onChange={handleTableChange}
          />
        </div>
      </Card>

      {/* Add/Edit User Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <UserOutlined style={{ color: "#1890ff" }} />
            {ownerType === "users" ? (modalType === "add"
              ? lang("Add_User") || "Add User"
              : lang("Edit_User") || "Edit User") : (modalType === "add"
                ? lang("Add_Merchant") || "Add Merchant"
                : lang("Edit_Merchant") || "Edit Merchant")}
          </div>
        }
        open={modalVisible && modalType !== "view"}
        onCancel={() =>
          !createLoading && !updateLoading && setModalVisible(false)
        }
        footer={null}
        width={ownerType === "users" ? 700 : 780}
        confirmLoading={createLoading || updateLoading}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
        >
          {ownerType === "users" ? (
            <UserFormFields
              form={form}
              image={image}
              handleImage={handleImage}
              handleRemoveImage={handleRemoveImage}
              modalType={modalType}
              FileType={FileType}
              phoneCountryIso={phoneCountryIso}
              setCountryCode={setCountryCode}
              setPhoneCountryIso={setPhoneCountryIso}
              setPhoneCountry={setPhoneCountry}
              getPhoneExample={getPhoneExample}
              validateMinimumAge={validateMinimumAge}
              maxAllowedDob={maxAllowedDob}
            />
          ) : (
            <MerchantFormFields
              form={form}
              logo={logo}
              handleLogo={handleLogo}
              handleRemoveLogo={handleRemoveLogo}
              cover={cover}
              handleCover={handleCover}
              handleRemoveCover={handleRemoveCover}
              doc={doc}
              handleDoc={handleDoc}
              handleRemoveDocument={handleRemoveDocument}
              modalType={modalType}
              FileType={FileType}
              merchantFileType={merchantFileType}
              userOptions={userOptions}
              userLoading={userLoading}
              page={page}
              hasMore={hasMore}
              search={search}
              setSearch={setSearch}
              setPage={setPage}
              setHasMore={setHasMore}
              handleUserSearch={handleUserSearch}
              fetchUsersForMerchant={fetchUsersForMerchant}
              debouncedUserSearch={debouncedUserSearch}
            />
          )}
          {/* Common Fields */}
          <Form.Item name="isActive" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item style={{ textAlign: "right", marginTop: 16 }}>
            <Space>
              <Button onClick={resetUserModalState}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                {modalType === "add" ? "Create" : "Update"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete User Modal */}
      <DeleteConfirmationModal
        visible={deleteModalVisible}
        key={userToDelete?.id || userToDelete?._id || "delete-modal"}
        onCancel={resetDeleteModal}
        onConfirm={(reason) => {
          handleDeleteUser(userToDelete, reason);
          resetDeleteModal();
        }}
        title={"Delete User"}
        message={"Are you sure you want to delete this user ?"}
        itemName={userToDelete?.name || userToDelete?.fullName}
        loading={
          userToDelete && deleteLoading[userToDelete._id || userToDelete.id]
        }
        okText={lang("Delete") || "Delete"}
        cancelText={lang("Cancel") || "Cancel"}
        input={true}
      />
      {/* View User Profile Modal */}
      {ownerType === "users" ? <UserProfileModal
        open={(modalType === "view" && modalVisible) && currentAdminRole?.can_view}
        onClose={resetUserModalState}
        onEdit={() => handleEditUser(selectedUser)}
        user={selectedUser}
        loading={createLoading || updateLoading}
        mapStatusToBoolean={mapStatusToBoolean}
        lang={lang}
        onViewWallet={handleWalletModal}
        ownerType={ownerType}
      /> : <MerchantProfileModal
        open={(modalType === "view" && modalVisible) && currentAdminRole?.can_view}
        onClose={resetUserModalState}
        onEdit={() => handleEditUser(selectedUser)}
        merchant={selectedUser}
        loading={createLoading || updateLoading}
        lang={lang}
        onViewWallet={handleWalletModal}
        onUpdateAdminComments={handleUpdateMerchantAdminComments}
        adminCommentsUpdateLoading={merchantCommentUpdateLoading}
        hasTrustAccountPermission={hasTrustAccountPermission}
      />}

      {/* Wallet Management Modal */}
      <WalletModal
        open={walletModalVisible}
        user={selectedUser}
        wallet={walletData}
        transactions={transactions}
        loading={walletLoading}
        onClose={() => setWalletModalVisible(false)}
        onAddFunds={handleAddFunds}
        addFundsLoading={addFundsLoading}
      />

      {/* Update Password Modal */}
      <UpdatePasswordModal
        open={changePasswordModal}
        onClose={() => setChangePasswordModal(false)}
        onSubmit={handleUpdateUserPassword}
        loading={updateLoading}
        form={form2}
        lang={lang}
      />
    </div>
  );
}

const UserFormFields = ({ form, image, handleImage, handleRemoveImage, modalType, FileType, phoneCountryIso, setCountryCode, setPhoneCountryIso, setPhoneCountry, getPhoneExample,
  validateMinimumAge,
  maxAllowedDob }) => {
  return (
    <>

      <Row gutter={16}>
        <Col span={24} md={12}>
          <Form.Item
            name="first_name"
            label={"First Name"}
            rules={[
              {
                required: true,
                whitespace: true,
                message: "Please enter user first name",
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (value !== value.trim()) {
                    return Promise.reject(
                      new Error("Leading or trailing spaces are not allowed"),
                    );
                  }
                  return Promise.resolve();
                },
              },
              {
                pattern: /^[A-Za-z0-9 _-]+$/,
                message: "Special characters are not allowed",
              },
              {
                max: 100,
                message:
                  lang("Name_must_be_100_characters_or_less") ||
                  "Name must be 100 characters or less",
              },
            ]}
          >
            <Input placeholder={"Enter user first name"} />
          </Form.Item>
        </Col>
        <Col span={24} md={12}>
          <Form.Item
            name="middle_name"
            label={"Middle Name"}
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (value !== value.trim()) {
                    return Promise.reject(
                      new Error("Leading or trailing spaces are not allowed"),
                    );
                  }
                  return Promise.resolve();
                },
              },
              {
                pattern: /^[A-Za-z0-9 _-]+$/,
                message: "Special characters are not allowed",
              },
              {
                max: 100,
                message:
                  lang("Name_must_be_100_characters_or_less") ||
                  "Name must be 100 characters or less",
              },
            ]}
          >
            <Input placeholder={"Enter user middle name"} />
          </Form.Item>
        </Col>
        <Col span={24} md={12}>
          <Form.Item
            name="last_name"
            label={"Last Name"}
            rules={[
              {
                required: true,
                whitespace: true,
                message: "Please enter user last name",
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (value !== value.trim()) {
                    return Promise.reject(
                      new Error("Leading or trailing spaces are not allowed"),
                    );
                  }
                  return Promise.resolve();
                },
              },
              {
                pattern: /^[A-Za-z0-9 _-]+$/,
                message: "Special characters are not allowed",
              },
              {
                max: 100,
                message:
                  lang("Name_must_be_100_characters_or_less") ||
                  "Name must be 100 characters or less",
              },
            ]}
          >
            <Input placeholder={"Enter user last name"} />
          </Form.Item>
        </Col>
        <Col span={24} md={12}>
          <Form.Item
            name="pronoun"
            label="Salutation"
            rules={[{ message: "Please select salutation" }]}
          >
            <Select
              placeholder="Select salutation"
              options={PRONOUN_OPTIONS}
            />
          </Form.Item>
        </Col>
        <Col span={24} md={12}>
          <Form.Item
            name="gender"
            label="Gender"
            rules={[{ message: "Please select gender" }]}
          >
            <Select
              placeholder="Select gender"
              options={GENDER_OPTIONS}
              onChange={(val) => form.setFieldsValue({ gender: val })}
            />
          </Form.Item>
        </Col>
        <Col span={24} md={12}>
          <Form.Item
            name="user_type"
            label="User Type"
            initialValue={USER_TYPE_OPTIONS[0].value}
          >
            <Select
              placeholder="Select User Type"
              options={USER_TYPE_OPTIONS}
            />
          </Form.Item>
        </Col>
        <Col span={24} md={12}>
          <Form.Item
            name="email"
            label={lang("Email") || "Email"}
            rules={[
              { required: true, message: "Please enter email" },
              {
                validator(_, value) {
                  if (!value) return Promise.resolve();

                  // Trim spaces
                  const email = value.trim();

                  // ❌ basic structure
                  if (!email.includes("@")) {
                    return Promise.reject(
                      new Error("Invalid email format"),
                    );
                  }

                  const [local, domain] = email.split("@");

                  // ❌ local part checks
                  if (
                    !local ||
                    local.startsWith(".") ||
                    local.endsWith(".")
                  ) {
                    return Promise.reject(
                      new Error("Invalid email local part"),
                    );
                  }

                  // ❌ domain checks
                  if (
                    !domain ||
                    domain.startsWith(".") ||
                    domain.endsWith(".") ||
                    domain.includes("..")
                  ) {
                    return Promise.reject(
                      new Error("Invalid email domain"),
                    );
                  }

                  // ❌ allowed characters (RFC-safe)
                  const emailRegex =
                    /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,24}$/;

                  if (!emailRegex.test(email)) {
                    return Promise.reject(
                      new Error("Invalid email address"),
                    );
                  }

                  // ❌ TLD sanity check
                  const domainParts = domain.split(".");
                  const tld = domainParts[domainParts.length - 1];

                  if (tld.length < 2 || tld.length > 24) {
                    return Promise.reject(
                      new Error("Invalid email extension"),
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder={lang("Enter_email") || "Enter email"} />
          </Form.Item>
        </Col>
        <Col span={24} md={12}>
          <Form.Item
            name="mobile"
            label="Mobile Number"
            help={
              phoneCountryIso
                ? `Example: ${getPhoneExample(phoneCountryIso)}`
                : null
            }
            rules={[
              { required: true, message: "Please enter your phone number" },
              () => ({
                validator(_, value) {
                  if (!value || !phoneCountryIso) return Promise.resolve();

                  // remove non-digits
                  const digitsOnly = value.replace(/\D/g, "");
                  const phoneNumber = parsePhoneNumberFromString(
                    "+" + digitsOnly,
                  );

                  if (!phoneNumber || !phoneNumber.isValid()) {
                    return Promise.reject(
                      new Error(
                        "Invalid phone number for selected country",
                      ),
                    );
                  }

                  return Promise.resolve();
                },
              }),
            ]}
          >
            <PhoneInput
              country="nz"
              disableDropdown
              onlyCountries={["nz"]}
              value={`${form.getFieldValue("mobile")}`}
              onChange={(value, country) => {
                form.setFieldsValue({ mobile: value });
                setCountryCode("64");
                setPhoneCountryIso("NZ");
                setPhoneCountry({ dialCode: "64" });
              }}
              inputStyle={{
                width: "100%",
                height: "49px",
              }}
            />
          </Form.Item>
        </Col>

        <Col span={24} md={24}>
          <Form.Item
            className=""
            label="Upload Image"
            rules={[
              {
                validator: (_, value) => {
                  if (value !== undefined && value?.length > 0) {
                    return Promise.resolve();
                  }
                  // return Promise.reject(new Error(lang("Image is required")));
                  return Promise.resolve();
                },
              },
            ]}
            name="image"
          >
            <SingleImageUpload
              value={image}
              fileType={FileType}
              imageType={"Profile"}
              btnName={"Image"}
              onChange={(data) => handleImage(data)}
              isDimension={false}
            />
          </Form.Item>
          <p className="img-size-details">
            You can select only JPEG, JPG and PNG files upto 5 MB file size
          </p>
          {image && (
            <div
              className="mt-2 add-img-product"
              style={{ display: "flex", gap: 12, alignItems: "center" }}
            >
              <Image width={120} src={image ?? notfound} />

              {modalType === "edit" && (
                <Button danger size="small" onClick={handleRemoveImage}>
                  Remove Image
                </Button>
              )}
            </div>
          )}
        </Col>
        <Col span={24} md={24}>
          <Form.Item
            name="date_of_birth"
            label="Date of Birth"
            rules={[
              {
                required: true,
                message: "Please select date of birth",
              },
              { validator: validateMinimumAge },
            ]}
          >
            <DatePicker
              style={{ width: "100%" }}
              disabledDate={(current) =>
                current && current > maxAllowedDob
              }
            />
          </Form.Item>
        </Col>
      </Row>

    </>
  )
}



const MerchantFormFields = ({
  form,
  logo,
  handleLogo,
  handleRemoveLogo,
  cover,
  handleCover,
  handleRemoveCover,
  doc,
  handleDoc,
  handleRemoveDocument,
  modalType,
  FileType,
  merchantFileType,
  userOptions,
  userLoading,
  page,
  hasMore,
  search,
  setSearch,
  setPage,
  setHasMore,
  fetchUsersForMerchant,
}) => {
  const handleUserSearch = (value) => {
    setSearch(value);
    setPage(1);
    setHasMore(true);
  };

  const handleScroll = (e) => {
    const target = e.target;
    if (
      target.scrollTop + target.offsetHeight >= target.scrollHeight - 10 &&
      hasMore &&
      !userLoading
    ) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchUsersForMerchant(search, nextPage);
    }
  };

  return (
    <div style={{ padding: "8px 4px" }}>
      {/* ================= BUSINESS INFO ================= */}
      <Card size="small" title="Business Information" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 18]}>
          <Col span={24} md={12}>
            <Form.Item
              name="nzbn"
              label="NZBN"
              rules={[
                { required: true, message: "NZBN is required" },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    if (!/^[0-9]+$/.test(value)) {
                      return Promise.reject("Only digits allowed");
                    }
                    if (value.length !== 13) {
                      return Promise.reject("Must be 13 digits");
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder="Enter NZBN" maxLength={13} disabled={modalType === "edit"} />
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item
              name="business_name"
              label="Business Name"
              rules={[{ required: true, message: "Enter business name" }]}
            >
              <Input placeholder="Enter business name" />
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item
              name="business_type"
              label="Business Type"
              rules={[{ required: true, message: "Enter business type" }]}
            >
              <Input placeholder="e.g. Retail, Food" />
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item
              name="refund_policy"
              label="Refund Policy (Days)"
              rules={[{ required: true, message: "Enter refund days" }]}
            >
              <Input type="number" placeholder="e.g. 7" />
            </Form.Item>
          </Col>

          {modalType === "edit" && (
            <Col span={24} md={12}>
              <Form.Item
                name="kyb_status"
                label="KYB Verification Status"
                tooltip="Update status in case automated KYB failed or requires manual approval"
              >
                <Select
                  options={[
                    { label: "COMPLETED (Verified)", value: "COMPLETED" },
                    { label: "PENDING (Pending Review)", value: "PENDING" },
                    { label: "UNDER REVIEW", value: "UNDER_REVIEW" },
                    { label: "FAILED", value: "FAILED" },
                    { label: "REJECTED", value: "REJECTED" },
                    { label: "EXPIRED", value: "EXPIRED" },
                    { label: "CANCELLED", value: "CANCELLED" },
                  ]}
                />
              </Form.Item>
            </Col>
          )}

          <Col span={24}>
            <Form.Item
              name="user_id"
              label="User"
              rules={[{ required: true, message: "Select user" }]}
            >
              <Select
                showSearch
                allowClear
                placeholder="Search and select user"
                filterOption={false}
                onSearch={handleUserSearch}
                onPopupScroll={handleScroll}
                options={userOptions}
                loading={userLoading}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="admin_comments"
              label="Admin Notes / Internal Comments"
              tooltip="Internal notes about KYB verification, director/shareholder corrections, or manual review"
            >
              <Input.TextArea
                rows={2}
                placeholder="Optional internal notes about this merchant or KYB verification..."
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* ================= DIRECTORS & GOVERNANCE ================= */}
      <Card
        size="small"
        title={
          <Space>
            <IdcardOutlined style={{ color: "#1890ff" }} />
            <span>Directors & Governance</span>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: "12px", marginBottom: 12 }}>
          List of company directors. In case automated KYB verification fails or requires manual corrections, update director details here.
        </Typography.Paragraph>

        <Form.List name="directors">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }, index) => (
                <Row key={key} gutter={[12, 8]} align="middle" style={{ marginBottom: 8 }}>
                  <Col flex="auto">
                    <Form.Item
                      {...restField}
                      name={[name, "name"]}
                      rules={[{ required: true, message: "Please enter director full name" }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        prefix={<UserOutlined style={{ color: "#bfbfbf" }} />}
                        placeholder={`Director ${index + 1} Full Name`}
                      />
                    </Form.Item>
                  </Col>
                  <Col flex="40px">
                    <Tooltip title="Remove Director">
                      <Button
                        type="text"
                        danger
                        icon={<Trash2 size={16} />}
                        onClick={() => remove(name)}
                      />
                    </Tooltip>
                  </Col>
                </Row>
              ))}

              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
                style={{ marginTop: fields.length > 0 ? 8 : 0 }}
              >
                Add Director
              </Button>
            </>
          )}
        </Form.List>
      </Card>

      {/* ================= SHAREHOLDERS & OWNERSHIP ================= */}
      <Card
        size="small"
        title={
          <Space>
            <TeamOutlined style={{ color: "#52c41a" }} />
            <span>Shareholders (Equity & Ownership)</span>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: "12px", marginBottom: 12 }}>
          List of company shareholders with equity ownership. In case KYB fails, update shareholder details and share percentages here.
        </Typography.Paragraph>

        <Form.List name="share_holders">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }, index) => (
                <Row key={key} gutter={[12, 8]} align="middle" style={{ marginBottom: 8 }}>
                  <Col span={14}>
                    <Form.Item
                      {...restField}
                      name={[name, "name"]}
                      rules={[{ required: true, message: "Please enter shareholder name" }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        prefix={<UserOutlined style={{ color: "#bfbfbf" }} />}
                        placeholder={`Shareholder ${index + 1} Full Name`}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      {...restField}
                      name={[name, "percent_holding"]}
                      rules={[
                        { required: true, message: "Share % required" },
                        {
                          validator: (_, value) => {
                            if (value === undefined || value === null || value === "") return Promise.resolve();
                            const num = Number(value);
                            if (isNaN(num) || num < 0 || num > 100) {
                              return Promise.reject("0-100% only");
                            }
                            return Promise.resolve();
                          }
                        }
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step="0.01"
                        suffix="%"
                        placeholder="Share %"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={2} style={{ textAlign: "center" }}>
                    <Tooltip title="Remove Shareholder">
                      <Button
                        type="text"
                        danger
                        icon={<Trash2 size={16} />}
                        onClick={() => remove(name)}
                      />
                    </Tooltip>
                  </Col>
                </Row>
              ))}

              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
                style={{ marginTop: fields.length > 0 ? 8 : 0 }}
              >
                Add Shareholder
              </Button>
            </>
          )}
        </Form.List>
      </Card>
      
      {/* ================= INVOICING INFO ================= */}
      <Card size="small" title="Invoicing Details" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 18]}>
          <Col span={24} md={12}>
            <Form.Item
              name="invoicing_name"
              label="Invoicing Name"
            >
              <Input placeholder="Leave blank to use Business Name" />
            </Form.Item>
          </Col>
          <Col span={24} md={12}>
            <Form.Item
              name="gst_number"
              label="GST Number"
            >
              <Input placeholder="Enter GST Number" />
            </Form.Item>
          </Col>
          <Col span={24} md={24}>
            <Form.Item
              name="invoicing_email"
              label="Billing Email"
              rules={[
                { type: "email", message: "Enter a valid email" }
              ]}
            >
              <Input placeholder="Enter email for invoices" />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* ================= MEDIA ================= */}
      <Card size="small" title="Media Uploads">
        <Row gutter={[16, 18]}>
          {/* LOGO */}
          <Col span={24} md={12}>
            <Form.Item name="business_logo" label="Business Logo">
              <SingleImageUpload
                value={logo}
                fileType={FileType}
                imageType="Logo"
                btnName="Logo"
                onChange={handleLogo}
              />
            </Form.Item>

            <Text type="secondary">
              JPG/PNG only, max 5MB
            </Text>

            {logo && (
              <div style={{ marginTop: 10 }}>
                <Space>
                  <Image width={100} src={logo} />
                  {modalType === "edit" && (
                    <Button danger size="small" onClick={handleRemoveLogo}>
                      Remove
                    </Button>
                  )}
                </Space>
              </div>
            )}
          </Col>

          {/* COVER */}
          <Col span={24} md={12}>
            <Form.Item name="business_cover_img" label="Cover Image">
              <SingleImageUpload
                value={cover}
                fileType={FileType}
                imageType="Cover"
                btnName="Cover"
                onChange={handleCover}
              />
            </Form.Item>

            <Text type="secondary">
              JPG/PNG only, max 5MB
            </Text>

            {cover && (
              <div style={{ marginTop: 10 }}>
                <Space>
                  <Image width={120} src={cover} />
                  {modalType === "edit" && (
                    <Button danger size="small" onClick={handleRemoveCover}>
                      Remove
                    </Button>
                  )}
                </Space>
              </div>
            )}
          </Col>

          {/* DOCUMENT */}
          <Col span={24}>
            <Form.Item name="registration_doc" label="Registration Document">
              <SingleImageUpload
                value={doc}
                fileType={merchantFileType}
                imageType="Document"
                btnName="Document"
                onChange={handleDoc}
              />
            </Form.Item>

            <Text type="secondary">
              JPG, PNG, PDF up to 5MB
            </Text>

            {doc && (
              <div style={{ marginTop: 10 }}>
                <Space>
                  {doc.endsWith(".pdf") ? (
                    <a href={doc} target="_blank" rel="noreferrer">📄 View PDF</a>
                  ) : (
                    <Image width={120} src={doc} />
                  )}
                  {modalType === "edit" && (
                    <Button danger size="small" onClick={handleRemoveDocument}>
                      Remove
                    </Button>
                  )}
                </Space>
              </div>
            )}
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default UserManager;